/* Ott-common */
import {
  schema,
  createEntity,
  compose,
  shape,
  string,
  boolean,
  number,
  arrayOf,
} from '@dtvgo/typed-normalizr';

import { EntityName, PROVIDER } from './constants';
import {
  Entity,
  Named,
  URLsObject,
  Playable,
  LiveContent,
  WithReleaseYear,
  WithSeasonNumber,
  MergedContent,
  WithDuration,
  WithShowableData,
  WithMetadata,
  WithPictureId,
  WithPictures,
  WithLabels,
} from './interfaces';
import {
  normalizeIdFields,
  normalizeResourceType,
  normalizePictures,
  normalizePictureId,
  normalizeAssetId,
  normalizeEpisodeAttributes,
  normalizeReleaseYear,
  normalizeSeasonCount,
  normalizeGenres,
  schemaAttribute,
  sortSeasonsList,
  sortFeaturedSection,
  sortEpisodes,
  normalizeBlimSection,
  normalizeOrbisSection,
  normalizeSourceProvider,
  normalizeEpisodeCount,
  normalizeBlimEpisodes,
  normalizeSeasons,
  normalizeDuration,
  normalizeBlimAsset,
  normalizeLiveResourceType,
  normalizeBlimStation,
  normalizeName,
  normalizeBlimLiveContentId,
  normalizeTAMostWatched,
} from './utils';

export const AssetEntity = createEntity(EntityName.ASSET, {
  ...Entity,
  ...WithDuration,
  contentProvider: shape({
    id: string(),
    category: string(),
    assetID: string(),
  }),
  assetId: number(),
  videoId: number(),
  liveURLs: shape(URLsObject),
  vodURLs: shape(URLsObject),
}, {
  preDeserialize: compose(
    normalizeIdFields,
    normalizeSourceProvider,
    normalizeBlimAsset,
  ),
});

export const VodContentEntity = createEntity(EntityName.VOD_CONTENT, {
  ...MergedContent,
  ...Playable,
  progress: number(),
  moreLikeThis: [],
}, {
  idAttribute(value) {
    return value.id || value.assetId;
  },
  preDeserialize: compose(
    normalizeIdFields,
    normalizeSourceProvider,
    normalizeResourceType,
    normalizePictures,
    normalizeAssetId,
    normalizeReleaseYear,
    normalizeEpisodeAttributes,
    normalizeGenres,
    normalizeDuration,
  ),
});

export const SeasonEntity = createEntity(EntityName.SEASON, {
  ...Entity,
  ...Named,
  ...WithReleaseYear,
  ...WithSeasonNumber,
  sourceProvider: string(),
  hasPublishedEpisodes: boolean(true),
  episodeCount: number(),
  episodes: [VodContentEntity],
}, {
  preDeserialize: compose(
    normalizeIdFields,
    normalizeSourceProvider,
    normalizeEpisodeCount,
    normalizeBlimEpisodes,
    normalizeReleaseYear,
    sortEpisodes,
  ),
});

export const ShowEntity = createEntity(EntityName.SHOW, {
  ...WithShowableData,
  sourceProvider: string(),
  seasonCount: number(),
  seasonsList: [SeasonEntity],
  moreLikeThis: [],
}, {
  preDeserialize: compose(
    normalizeIdFields,
    normalizeSourceProvider,
    normalizeResourceType,
    normalizePictures,
    normalizeReleaseYear,
    normalizeSeasonCount,
    normalizeGenres,
    normalizeSeasons,
    sortSeasonsList,
  ),
});

export const withMoreLikeThis = new schema.Array({
  [EntityName.SHOW]: ShowEntity,
  [EntityName.VOD_CONTENT]: VodContentEntity,
}, schemaAttribute);

ShowEntity.define({ moreLikeThis: withMoreLikeThis });
VodContentEntity.define({ moreLikeThis: withMoreLikeThis });

export const LiveContentEntity = createEntity(EntityName.LIVE_CONTENT, {
  ...MergedContent,
  ...LiveContent,
  channelNumber: string(),
}, {
  idAttribute(liveContent) {
    return normalizeBlimLiveContentId(liveContent).id;
  },
  preDeserialize: compose(
    normalizeIdFields,
    normalizeSourceProvider,
    normalizeLiveResourceType,
    normalizeDuration,
    normalizeReleaseYear,
    normalizeEpisodeAttributes,
    normalizeGenres,
    normalizeName,
    normalizeBlimLiveContentId,
  ),
});

export const StationEntity = createEntity(EntityName.STATION, {
  ...Entity,
  ...WithLabels,
  ...Named,
  ...WithMetadata,
  ...Playable,
  ...WithPictureId,
  ...WithPictures,
  entitled: boolean(),
  schedules: new schema.Values(LiveContentEntity),
}, {
  idAttribute(value) {
    return value.id || value.stationId;
  },
  preDeserialize: compose(
    normalizeIdFields,
    normalizeAssetId,
    normalizePictureId,
    ({ schedules, ...data }) => {
      if (!schedules) return data;

      const schedulesObject = {};
      schedules.forEach((schedule) => {
        schedulesObject[schedule.startTime] = schedule;
      });

      return {
        schedules: schedulesObject,
        ...data,
      };
    },
    ({ stationId, ...data }) => ({
      id: stationId,
      ...data,
    }),
  ),
});

export const BlimStationEntity = createEntity(EntityName.BLIM_STATION, {
  ...Entity,
  ...Named,
  ...WithPictureId,
  stationId: string(),
  entitled: boolean(),
  schedules: new schema.Values(LiveContentEntity),
  channelNumber: number(),
  stationNumber: number(),
  liveURLs: shape(URLsObject),
}, {
  idAttribute(value) {
    return value.channelNumber || value.stationNumber;
  },
  preDeserialize: compose(
    normalizeBlimStation,
    normalizeIdFields,
    normalizeSourceProvider,
    normalizeBlimAsset,
    normalizePictureId,
    ({ schedules, ...station }) => {
      if (!schedules) return station;

      const schedulesObject = {};
      schedules.forEach((schedule) => {
        schedulesObject[schedule.startTime] = {
          ...schedule,
          sourceProvider: PROVIDER.BLIM,
          channelNumber: station.id,
        };
      });

      return {
        schedules: schedulesObject,
        ...station,
      };
    },
  ),
});

export const StationCategoryEntity = createEntity(EntityName.STATION_CATEGORY, {
  id: number(),
  name: string(),
  stations: arrayOf(string()),
});

export const BannerEntity = createEntity(EntityName.BANNER, {
  ...Entity,
  ...WithLabels,
  ...Named,
  ...WithPictureId,
  overlayImage: string(),
  actions: arrayOf(shape({
    target: string(),
    text: string(),
    type: number(),
    link: string(),
  })),
}, {
  preDeserialize: normalizeIdFields,
});

export const SectionEntity = createEntity(EntityName.SECTION, {
  id: string(),
  name: string(),
  sourceProvider: string(),
  label: string(),
  type: string(),
  items: new schema.Array({
    [EntityName.BANNER]: BannerEntity,
    [EntityName.SHOW]: ShowEntity,
    [EntityName.STATION]: StationEntity,
    [EntityName.VOD_CONTENT]: VodContentEntity,
    [EntityName.LIVE_CONTENT]: LiveContentEntity,
  }, schemaAttribute),
}, {
  idAttribute(value) {
    return value.id || value.sectionId || value.sectionID || value.carouselId;
  },
  preDeserialize: compose(
    normalizeIdFields,
    normalizeSourceProvider,
    (currentSection) => {
      const sourceProvider = currentSection.sourceProvider || PROVIDER.ORBIS;

      if (sourceProvider === PROVIDER.BLIM) {
        return normalizeBlimSection({ ...currentSection, sourceProvider });
      }

      if (currentSection.isTAMostWatched) {
        return normalizeTAMostWatched({
          ...currentSection,
          sourceProvider,
        });
      }

      return normalizeOrbisSection({ ...currentSection, sourceProvider });
    },
  ),
});

export const PageEntity = createEntity(EntityName.PAGE, {
  id: string(),
  title: string(),
  sections: [SectionEntity],
}, {
  preDeserialize: sortFeaturedSection,
});
